import axios from 'axios';
import { BACKEND_URL } from './backendUrl';

export { BACKEND_URL };

const API_URL = `${BACKEND_URL}/api`;

/**
 * Build a dynamic base URL for any service.
 * - Checks for an env-var override first.
 * - In local dev, points to the given devPort on the current hostname.
 * - In production, uses the relative `path` (served via reverse proxy).
 */
const getServiceUrl = (envVar, devPort, path) => {
  const isBrowserOnLocalhost = typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const envVarPointsToLocalhost = envVar && /^https?:\/\/(localhost|127\.0\.0\.1)(:|\/)/i.test(envVar);

  // A localhost env value only makes sense when the page itself is being viewed
  // from localhost (real local dev). If the site is deployed with a stale/dev
  // .env that still says "localhost", ignore it rather than sending every real
  // visitor's browser to fetch their own machine — fall through to the
  // production relative-path branch below instead.
  if (envVar && !(envVarPointsToLocalhost && !isBrowserOnLocalhost)) {
    return envVar;
  }

  if (typeof window === 'undefined') return `http://localhost:${devPort}${path}`;

  const { hostname, port } = window.location;
  const isIP = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(hostname);

  if (isBrowserOnLocalhost) {
    return `http://localhost:${devPort}${path}`;
  }
  if (isIP || port === '3000') {
    return `http://${hostname}:${devPort}${path}`;
  }

  return path;
};

export const RAG_BASE_URL = getServiceUrl(
  process.env.REACT_APP_RAG_API_URL, 8100, '/api/rag'
);

export const OSINT_BASE_URL = getServiceUrl(
  process.env.REACT_APP_OSINT_API_URL, 8100, '/osint'
);

const api = axios.create({
  baseURL: API_URL,
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    config.headers['ngrok-skip-browser-warning'] = '69420';
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401s
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error?.config?.url || '';
    const isUploadRequest = url.includes('/uploads/cloudinary');
    if (error.response && error.response.status === 401 && !isUploadRequest) {
      // Token expired or invalid
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;
